/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import Ad from 'components/Ad'
import BlogCard from 'components/cards/BlogCard'
import Comments from 'components/comments'
import DiscussOnBlogButtons from 'components/DiscussOnBlogButtons'
import LikeButton from 'components/LikeButton'
import Link from 'components/Link'
import TextDataLoader from 'components/loaders/TextDataLoader'
import Newsletter from 'components/Newsletter'
import ScrollTopAndComment from 'components/ScrollTopAndComment'
import { ArticleSEO } from 'components/SEO'
import ShareArticle from 'components/ShareArticle'
import Tag from 'components/Tag'
import PageTitle from 'components/text/PageTitle'
import TOCInline from 'components/TOCInline'
import type { Author, Post } from 'contentlayer/generated'
import siteMetadata from 'data/siteMetadata'
import useViewCounter from 'hooks/useViewCounter'
import { trackEvent } from 'lib/utils/analytics'
import { formatDate } from 'lib/utils/date'
import { FunctionComponent, ReactNode } from 'react'
import { Toc } from 'types/Toc'

interface Props {
  content: Post
  suggestedPosts: Post[]
  children: ReactNode
  authorDetails: Author[]
  next?: { slug: string; title: string }
  prev?: { slug: string; title: string }
}

const PostLayout: FunctionComponent<Props> = ({
  content: post,
  suggestedPosts = [],
  authorDetails,
  next,
  prev,
  children,
}) => {
  const { slug, date, lastmod, title, coverUrl, summary, discuss, tags, toc, readingTime } = post
  const url = `${siteMetadata.siteUrl}${slug}`

  const { data, isLoading } = useViewCounter({ slug, type: 'blog' })

  const onNavigateBlogClick = (direction = 'Next', articleSlug = '') => {
    trackEvent({ name: 'Navigate Post', data: { direction, slug: articleSlug } })
  }

  const onSuggestedBlogClick = (toSlug = '') => {
    trackEvent({ name: 'Suggested Post', data: { fromSlug: slug, toSlug } })
  }

  return (
    <>
      <ArticleSEO
        url={`${siteMetadata.siteUrl}${slug}`}
        title={title}
        summary={summary}
        authorDetails={authorDetails}
        date={date}
        coverUrl={coverUrl}
        lastmod={lastmod}
        readingTime={readingTime.text}
        key={title}
      />
      <ScrollTopAndComment />
      <div>
        <header>
          <div className="pb-10 space-y-6 text-center">
            <div>
              <PageTitle>{title}</PageTitle>
            </div>
            <div className="space-y-2">
              <dl>
                <div>
                  <dt className="sr-only">Published on</dt>
                  <dd className="text-lg font-medium leading-6 text-gray-700 dark:text-gray-200">
                    <time dateTime={date}>{formatDate(date)}</time>
                  </dd>
                </div>
              </dl>
              {lastmod && (
                <dl>
                  <div>
                    <dt className="sr-only">Updated on</dt>
                    <dd className="text-base text-gray-500 dark:text-gray-400">
                      <time dateTime={lastmod}>(Updated on {formatDate(lastmod)})</time>
                    </dd>
                  </div>
                </dl>
              )}
            </div>
          </div>
        </header>
        <hr className="hr" />
        <div className="mx-auto flex flex-col-reverse justify-evenly lg:flex-row lg:gap-6 lg:px-0 mb-8">
          <div className="lg:max-w-2xl lg:mx-auto">
            <div>
              <article className="pt-10 pb-8 prose dark:prose-dark max-w-none">{children}</article>
              <div>
                <DiscussOnBlogButtons discuss={discuss} />
              </div>
              <div className="mt-6">
                <Newsletter />
              </div>
              <div className="lg:hidden">
                <ShareArticle url={url} title={title} coverUrl={coverUrl} />
              </div>
              <div className="lg:hidden mt-4">
                <LikeButton slug={slug} type="blog" />
              </div>
            </div>
          </div>
          <aside className="pt-8 top-0 lg:sticky lg:h-full w-full lg:w-80">
            <div className="space-y-6">
              <div className="prose dark:prose-dark max-w-none">
                <TOCInline toc={(toc as unknown) as Toc} exclude="Overview" toHeading={2} />
              </div>
              <div className="hidden lg:block">
                <ShareArticle url={url} title={title} coverUrl={coverUrl} />
              </div>
              <div className="hidden lg:block">
                <LikeButton slug={slug} type="blog" />
              </div>
              <div className="h-fit">
                <Ad />
              </div>
            </div>
          </aside>
        </div>
        <Comments frontMatter={post} />
        <footer>
          <div className="divide-gray-200 text-sm font-medium leading-5 dark:divide-gray-700 xl:col-start-1 xl:row-start-2 xl:divide-y">
            <div className="flex justify-between">
              {tags && (
                <div className="py-4 xl:py-8">
                  <h2 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Tags
                  </h2>
                  <div className="flex flex-wrap">
                    {tags.map((tag) => (
                      <Tag key={tag} text={tag} />
                    ))}
                  </div>
                </div>
              )}
              <div className="py-4 xl:py-8">
                <h2 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Views
                </h2>
                {isLoading ? (
                  <TextDataLoader />
                ) : (
                  <span className="text-primary-700 dark:text-primary-500">
                    {Number(data.total || 0).toLocaleString()} views
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-4 py-6 text-sm font-medium sm:flex-row sm:justify-between sm:text-base">
              {Object.keys(prev).length > 0 && (
                <div className="max-w-sm">
                  <h2 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Previous Article
                  </h2>
                  <div className="text-primary-700 dark:text-primary-500 hover:text-primary-600 dark:hover:text-primary-400">
                    <Link
                      href={`${prev.slug}`}
                      onClick={() => onNavigateBlogClick('Prev', prev.slug)}
                    >
                      {prev.title}
                    </Link>
                  </div>
                </div>
              )}
              {Object.keys(next).length > 0 && (
                <div className="max-w-sm">
                  <h2 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Next Article
                  </h2>
                  <div className="text-primary-700 dark:text-primary-500 hover:text-primary-600 dark:hover:text-primary-400">
                    <Link
                      href={`${next.slug}`}
                      onClick={() => onNavigateBlogClick('Next', next.slug)}
                    >
                      {next.title}
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <div className="py-6 space-y-5">
              <h2 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                You may also like
              </h2>
              <ul className="mx-auto grid gap-10 md:grid-cols-2 lg:grid-cols-3 lg:max-w-none">
                {suggestedPosts.map((frontMatter, idx) => {
                  const { slug } = frontMatter
                  return (
                    <li
                      key={slug}
                      data-aos="fade-up"
                      data-aos-delay={idx > 2 ? 250 : (idx + 1) * 100}
                      onClick={() => onSuggestedBlogClick(slug)}
                    >
                      <BlogCard frontMatter={frontMatter} />
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
          <div className="pt-4 xl:pt-8">
            <Link
              href="/blogs"
              className="text-xl text-primary-700 dark:text-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
            >
              {'>'} cd ..
            </Link>
          </div>
        </footer>
      </div>
    </>
  )
}

export default PostLayout
